import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  PopupWindow,
  Button,
  InputList,
  ValidationStyle,
  validationEngineFromInputInformationList,
} from '@utahdts/dts-react-common';
import useRefLazy from '../../misc/hooks/useRefLazy';
import userInputs from '../inputs/userInputs';
import roles from '../../app/roles/roles';

const propTypes = {
  // The currently selected company (currentOrgRecord). Its FEIN is the org
  // business key the Org Administrator role is bound to; its name is shown.
  company: PropTypes.shape({
    fein: PropTypes.string,
    name: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const defaultProps = {
  company: undefined,
};

/**
 * Add an Org Administrator to the currently selected company by filling out the
 * required pharmacy-user fields (name, email, phone) and submitting. The role
 * (Org Administrator) and company are pre-populated and not editable. On submit
 * the caller (Users.jsx) either attaches the Org Administrator role to an
 * existing user (matched by the email business key) or creates a new user
 * document carrying that role. Existing users are never listed here.
 */
const AddOrgAdmin = ({ company, onClose, onSave }) => {
  const [isBusy, setBusy] = useState(false);
  const [inputValues, setInputValues] = useState({
    id: null,
    name: null,
    email: null,
    phone: null,
  });
  const inputValuesRef = useRef();
  inputValuesRef.current = inputValues;

  const [validationEngineStyle, setValidationEngineStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const validationEngineRef = useRef(undefined);

  // Required pharmacy-user fields (name, email, phone) with email-format and
  // required validation — the same input definitions the create-user form uses.
  const inputs = useRefLazy(() =>
    userInputs(
      {
        inputsOnChange: (field, value) =>
          setInputValues((oldValues) => ({ ...oldValues, [field]: value })),
      },
      true,
      true,
      true,
    ),
  );

  const [validationEngine, setValidationEngine] = useState(undefined);
  validationEngineRef.current = validationEngine;
  if (validationEngine) {
    validationEngine.validationStyle = validationEngineStyle;
  }

  useEffect(() => {
    setValidationEngine(
      validationEngineFromInputInformationList(inputs.current),
    );
  }, [inputs.current]);

  const companyName = (company && (company.name || company.fein)) || '';

  const submit = () => {
    setValidationEngineStyle(ValidationStyle.ALWAYS);
    if (
      validationEngineRef.current &&
      validationEngineRef.current.isValid(inputValuesRef.current)
    ) {
      setBusy(true);
      // onSave attaches the Org Administrator role to a matching existing user
      // or creates a new one; a rejection clears busy so the form can retry.
      Promise.resolve(onSave(inputValuesRef.current)).catch(() =>
        setBusy(false),
      );
    }
  };

  return (
    <PopupWindow
      className="popup-window--large"
      onCloseButtonCallback={onClose}
      footerChildren={[
        <Button
          label="Close"
          onClick={onClose}
          key="close-button"
          className="button--black"
        />,
        <Button
          label="Save"
          busy={isBusy}
          onClick={submit}
          key="save-button"
        />,
      ]}
    >
      <h3>Add Org Administrator</h3>
      <p>
        Add an <strong>{roles.orgAdmin}</strong> to{' '}
        <strong>{companyName}</strong>. If a user with the entered email already
        exists, the role is added to that user; otherwise a new user is created.
      </p>
      {/* Pre-populated, non-editable role: Org Administrator + the company. */}
      <div className="stack-form mb-spacing">
        <div className="input__label">Role</div>
        <div data-testid="locked-role" className="readonly-value">
          {roles.orgAdmin} &mdash; {companyName}
        </div>
      </div>
      <InputList
        inputs={Object.values(inputs.current)}
        data={inputValues}
        validationEngine={validationEngine}
        className="stack-form flex flex-wrap half-form"
      />
    </PopupWindow>
  );
};

AddOrgAdmin.propTypes = propTypes;
AddOrgAdmin.defaultProps = defaultProps;

export default AddOrgAdmin;
